import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { Edit3Icon, Eye } from 'lucide-react';
import { useCradSize } from '@/hooks/use-card-size';
import { Image } from '@/components/common/image';
import { TruncatedText } from '@/components/common/truncated-text';
import { CopyClipboard } from '@/components/common/copy-clipboard';
import { IconButton } from '@/components/common/icon-button';
import { TrashIcon } from '@/components/ui/icon';
import { DeleteConfirmModal, Tooltip } from "@/components/ui/modal";
import { TemplateVideoAddModal } from './template-video-add-modal';
import { TemplateDetailModal } from '@/components/modal-template-detail';
import { StyleData } from '@/services/agent-v2-style-list-local';
import { agentTemplateHooks, iamAccountHooks } from '@/manager';
import { PUSH_SETTINGS_TYPE, SettingTab } from '@/manager/manager/agent-dashboard-user-management-manager';
import { AddTemplateIcon, TemplateEmptyIcon } from './assets';
import { LoadingOverlay } from '../common/loading-overlay';
import StyleEditorMicroApp from '@bhb-frontend/style-editor-microapp';
import { AgentV2TemplateDetailData } from '@/services/agent-v2-template-detail';
import { TemplateDetailModalVideo } from '../modal-template-detail/index-video';
import { createPortal } from 'react-dom';
import { useStyleEditorMicroApp } from '@/hooks/use-style-editor-microapp';

// 骨架屏容器组件
function Skeleton() {
  return (
    <>
      {Array.from({ length: 8 }).map((_, index) => (
        <div key={index} className="w-full h-full relative">
          <div className="w-full aspect-[9/16] bg-gray-300 rounded-[12px] mb-[10px] animate-pulse"></div>
          <div className="absolute bottom-1 left-[10px] w-full text-start translate-y-full">
            <div className="h-4 bg-gray-300 rounded w-3/4 animate-pulse"></div>
            <div className="h-3 bg-gray-300 rounded w-2/4 mt-1 animate-pulse"></div>
          </div>
        </div>
      ))}
    </>
  );
}

interface TemplateVideoProps {
  actors: Actor[];
  voices: Audio[];
  activeTab: SettingTab;
}

/** 模板视频 */
export const TemplateVideoNew = ({ actors, voices, activeTab }: TemplateVideoProps) => {
  const { t } = useTranslation();

  const { ref, columns } = useCradSize({ min: 120, max: 192, gap: 3 });

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [triggerId, setTriggerId] = useState("");
  const [selectedStyle, setSelectedStyle] = useState<StyleData | null>(null);
  const { account } = iamAccountHooks.useData();

  // 模板相关
  const { templateList, loading, search } = agentTemplateHooks.useTemplateListData();
  const { loading: saveLoading, save } = agentTemplateHooks.useSaveAgentTemplate();
  const { loading: deleteLoading, deleteTemplate } = agentTemplateHooks.useDeleteAgentTemplate();
  const { loading: detailLoading, templateDetail, fetchDetail, resetDetail } = agentTemplateHooks.useTemplateDetail();

  const { loading: updateLoading, update } = agentTemplateHooks.useUpdateAgentTemplate();

  // 打开删除确认弹窗
  const handleDelete = (uuid: string) => {
    setDeleteModalOpen(true);
    setDeleteId(uuid);
  }

  // 确认删除模板
  const confirmDelete = async () => {
    await deleteTemplate(deleteId);
    setDeleteModalOpen(false);
  }

  // 根据 type 搜索模板
  const handleSearch = async () => {
    await search(1, PUSH_SETTINGS_TYPE[activeTab]);
  }

  useEffect(() => {
    handleSearch();
  }, [activeTab])

  // 查看模板详情
  const handleDetail = async (uuid: string) => {
    fetchDetail(uuid);
    setDetailModalOpen(true);
  }

  // 数字人
  const digitalHumans = actors.filter(item => item.generateStatus === 2).map((item, index) => ({
    id: item.id,
    name: item.name,
    coverUrl: item.image,
    renderUrl: item.image,
    width: item.width,
    height: item.height,
    modelId: item.modelId
  }))

  // 配音
  const voiceList = voices.map((item, index) => ({
    id: item.voiceTtsId,
    name: item.name,
    exampleUrl: item.audioUrl,
  }))


  // 微应用 Hook
  const {
    env,
    initialAppLoading,
    enablePresetTemplates,
    handleMicroAppMessage,
    handleMounted,
    handleUnmounted,
    openEditor
  } = useStyleEditorMicroApp({
    account,
    openPublicActors: true,
    openPublicVoices: true,
    actors: digitalHumans,
    voices: voiceList,
    onSaveSuccess: async ({ styleSaveParams, actorModelId }) => {
      // 新建
      if (!triggerId && selectedStyle) {
        setAddModalOpen(false);
        // 构建新建保存参数
        const createParams = {
          styleId: styleSaveParams.styleId,
          templateName: selectedStyle.name,
          actorModelId: actorModelId || digitalHumans?.find(item => item.id === styleSaveParams.digitalHumanId)?.modelId || '',
          voiceModelId: styleSaveParams.voiceId || '',
          coverUrl: styleSaveParams.coverUrl,
          speedRatio: styleSaveParams.speedRatio,
          scene: styleSaveParams.scene,
          templateId: styleSaveParams.templateId,
        }
        console.log('[miuta]构建创建请求参数', { createParams, styleSaveParams, enablePresetTemplates })
        await save(createParams);
      } else {
        // 构建更新保存参数
        const updateParams = {
          uuid: triggerId,
          styleId: styleSaveParams.styleId,
          coverUrl: styleSaveParams.coverUrl,
          speedRatio: styleSaveParams.speedRatio,
          scene: styleSaveParams.scene,
          actorModelId: actorModelId || digitalHumans?.find(item => item.id === styleSaveParams.digitalHumanId)?.modelId || '',
          voiceModelId: styleSaveParams.voiceId || '',
        }
        console.log('[miuta]构建更新请求参数', { updateParams, styleSaveParams, enablePresetTemplates })
        await update(updateParams);
      }
      setTriggerId('');
    },
    onClose: async () => {
      setTriggerId('');
    }
  });

  // 添加模板
  const handleAdd = async ({ data, actor }: { data: StyleData, actor: Actor }) => {
    setTriggerId('');
    setSelectedStyle(data);
    openStyleEditor({
      coverUrl: data.coverUrl,
      styleModelId: data.styleId,
      actorModelId: actor.modelId,
      actorName: actor.name,
      actorPicUrl: actor.image,
      voiceModelId: actor.audio.voiceTtsId,
      voiceName: actor.audio.name,
      voiceSampleUrl: actor.audio.audioUrl,
      templateName: data.name,
      coverVideo: data.demoUrl,
      speedRatio: actor.audio.speed,
      scene: data.scene,
    });
  }

  // 打开编辑模板
  const handleEdit = async (uuid?: string, template?: AgentV2TemplateDetailData) => {
    setTriggerId(uuid || template.uuid);
    if (uuid) {
      // 获取模板详情
      const detail = await fetchDetail(uuid);
      openStyleEditor(detail);
      return;
    }
    if (template) {
      openStyleEditor(template);
      return;
    }
  }

  // 打开模板编辑器
  const openStyleEditor = async (
    detail: Omit<AgentV2TemplateDetailData, 'uuid'>
  ) => {
    // 取出数字人信息
    const actorInfo = digitalHumans?.find(item => item.modelId === detail.actorModelId)
    // 处理编辑参数
    const editIdParam = {
      coverUrl: detail.coverUrl,
      styleId: detail.styleModelId,
      digitalHumanInfo: {
        name: detail.actorName,
        coverUrl: detail.actorPicUrl,
        renderUrl: detail.actorPicUrl,
        id: actorInfo?.id,
        width: actorInfo?.width,
        height: actorInfo?.height,
      },
      voiceInfo: {
        id: detail.voiceModelId,
        name: detail.voiceName,
        coverUrl: detail.coverUrl,
        exampleUrl: detail.voiceSampleUrl,
        speedRatio: detail.speedRatio,
      }
    }
    // 调起编辑模板弹窗
    openEditor({ editIdParam });
  }

  // 选择模板
  const handleSelectStyle = (style: StyleData | null) => {
    if (!style?.styleId) {
      console.error('Error: styleId is required', { style })
      return;
    }
    console.log('[Miuta]点击模板打开编辑器', { style })
    setSelectedStyle(style);
    openEditor({
      templateId: style?.styleId,
    })
  }

  return (
    <div className="w-full h-full flex flex-col relative">
      <div className='flex items-center justify-between text-base font-medium text-[rgba(0,0,0,1)] mb-5'>
        <span>{t("agent_dashboard.user_detail.template.title")}</span>
        {createPortal(
          <div
            className='z-[999999]'
            style={{ pointerEvents: 'auto' }}
            // 阻止滚动事件冒泡到 document/body
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <StyleEditorMicroApp
              name="style-editor-microapp"
              env={env}
              onMicroAppMessage={handleMicroAppMessage}
              onMounted={handleMounted}
              onUnmounted={handleUnmounted}
            />
          </div>,
          document.documentElement
        )}
        <Tooltip
          side='top'
          align='center'
          wrapperClassName='focus:outline-none'
          trigger={
            <button
              className='h-10 flex items-center justify-center py-[10px] px-[14px] border border-[rgba(213,215,218,1)] hover:bg-gray-50  rounded-md shadow-sm transition-all focus:outline-none'
              onClick={() => setAddModalOpen(true)}

            >
              <AddTemplateIcon />
              <span className='ml-1.5 text-sm text-[rgba(16,24,40,1)] font-semibold'>{t("agent_dashboard.user_detail.template.add_template")}</span>
            </button>
          }
          content={
            <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
              <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                {t("agent_dashboard.user_detail.template.add_template_desc")}
              </span>
              <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
            </div>
          }
        />
      </div>
      <div
        ref={ref}
        className='w-full grid gap-x-2 gap-y-18 relative'
        style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
      >
        {loading && <Skeleton />}
        {!loading && templateList.length > 0 ? templateList?.map((item, index) => (
          <div key={item.id} className="w-full h-full relative">
            <div
              className="group relative w-full aspect-[9/16] bg-gray-300 rounded-[12px] mb-[10px] hover:shadow-lg transition-all cursor-pointer select-none overflow-hidden"
              onClick={() => handleDetail(item.uuid)}
            >
              <div className='overflow-hidden flex-shrink-0 '>
                <Image src={item.coverUrl} className="size-full rounded-[12px] object-contain" />
                <LoadingOverlay className='bg-white/50' loading={(detailLoading || updateLoading) && triggerId === item.uuid} />
              </div>
              <div className='opacity-0 group-hover:opacity-100 absolute bottom-2 right-2 transition-all flex gap-1 bg-[rgba(0,0,0,0.6)] rounded-[8px] px-1 py-1'>
                <Tooltip
                  side='top'
                  align='center'
                  trigger={
                    <IconButton
                      className="hover:bg-[rgba(255,255,255,0.2)]"
                      icon={<Eye className="w-4 h-4 text-white" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDetail(item.uuid)
                      }}
                    />
                  }
                  content={
                    <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
                      <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                        {t("agent_dashboard.user_detail.template.view_detail")}
                      </span>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
                    </div>
                  }
                />
                <Tooltip
                  side='top'
                  align='center'
                  trigger={
                    <IconButton
                      className="hover:bg-[rgba(255,255,255,0.2)]"
                      icon={<Edit3Icon className="w-4 h-4 text-white" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(item.uuid)
                      }}
                    />
                  }
                  content={
                    <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2  rounded-[8px]'>
                      <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                        {t("agent_dashboard.user_detail.template.edit")}
                      </span>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
                    </div>
                  }
                />
                <Tooltip
                  side='top'
                  align='center'
                  trigger={
                    <IconButton
                      className="hover:bg-[rgba(255,255,255,0.2)]"
                      icon={<TrashIcon className="w-4 h-4 text-white" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item.uuid)
                      }}
                    />
                  }
                  content={
                    <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2  rounded-[8px]'>
                      <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                        {t("agent_dashboard.user_detail.template.delete")}
                      </span>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
                    </div>
                  }
                />
              </div>
            </div>
            <div className="absolute bottom-1 left-[10px] w-full text-start translate-y-full">
              <div className="w-full">
                <TruncatedText text={item.templateName} className='max-w-[90%] text-base font-medium text-[rgba(16,24,40,1)]' />
              </div>
              <div className="mt-1 flex items-center">
                <TruncatedText text={item.id} className='max-w-[80%] text-sm mr-1 text-[rgba(52,64,84,1)]' />
                <CopyClipboard text={item.id} />
              </div>
            </div>
          </div>
        )) : !loading && (
          <div className=' w-full h-full relative  flex flex-col justify-center items-center col-span-full mt-3 mb-16'>
            <TemplateEmptyIcon className='w-[159px] h-[136px]' />
            <span className='text-base text-[rgba(102,112,133,1)]'>{t("agent_dashboard.user_detail.template.template_empty")}</span>
          </div>
        )}
        <LoadingOverlay loading={saveLoading} />
      </div>
      <DeleteConfirmModal
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        onConfirm={confirmDelete}
        loading={deleteLoading}
      />

      {addModalOpen && (
        <TemplateVideoAddModal
          open={addModalOpen}
          onOpenChange={setAddModalOpen}
          actors={actors}
          voices={voices}
          onSave={handleAdd}
          loading={saveLoading || updateLoading}
          onSelectStyle={handleSelectStyle}
        />
      )}
      {detailModalOpen && (
        <TemplateDetailModalVideo
          actors={actors}
          voices={voices}
          template={templateDetail}
          loading={detailLoading}
          open={detailModalOpen}
          deleteTemplate={deleteTemplate}
          deleteLoading={deleteLoading}
          updateLoading={updateLoading}
          onVideoEdit={() => handleEdit(undefined, templateDetail)}
          onOpenChange={(value) => {
            setDetailModalOpen(value);
            if (!value) resetDetail();
          }}
        />
      )}
      {editModalOpen && (
        <TemplateVideoAddModal
          title={t("agent_dashboard.user_detail.template.edit_template")}
          open={editModalOpen}
          loading={updateLoading}
          selectedStyle={{
            styleId: templateDetail.styleModelId,
            name: templateDetail.templateName,
            coverUrl: templateDetail.coverUrl,
            demoUrl: templateDetail.coverVideo,
            scene: templateDetail.scene,
          }}
          selectedActor={actors.find(actor => actor.modelId === templateDetail.actorModelId) || null}
          actors={actors}
          voices={voices}
          // onSave={handleUpdate}
          onOpenChange={(value) => {
            setEditModalOpen(value);
            if (!value) resetDetail();
          }}
        />
      )}
    </div>
  )
}
